import { useState } from 'react';
import { Banknote, CircleMinus, CircleSlash, Clock, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  EXCESSIVE_ANNUAL_HOURS,
  STANDARD_DAY_HOURS,
  WATCH_ANNUAL_HOURS,
  formatNumber,
  leaveRiskTone,
  percent,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrPerson } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

type LeaveType = 'Annual' | 'Personal';

const TONE_COLOURS: Record<string, string> = {
  danger: 'var(--destructive)',
  warning: 'var(--warning)',
  serious: 'var(--destructive)',
  link: 'var(--chart-3)',
  muted: 'var(--muted-foreground)',
  success: 'var(--success)',
};

export function LeaveSection({
  people,
  filters,
  asOf,
  canExport,
  checkExport,
  toggleDept,
}: HrSectionProps) {
  const [leaveType, setLeaveType] = useState<LeaveType>('Annual');

  const nonCasual = people.filter((person) => person.emp !== 'Casual');
  const annualRows = nonCasual.filter((person) => person.lb_annual !== null);
  const balanceKey = leaveType === 'Annual' ? 'lb_annual' : 'lb_personal';
  const withBalance = nonCasual.filter((person) => person[balanceKey] !== null);

  const total = withBalance.reduce(
    (sum, person) => sum + ((person[balanceKey] as number) || 0),
    0
  );
  const average = withBalance.length > 0 ? total / withBalance.length : null;

  const excessive = annualRows.filter(
    (person) => (person.lb_annual as number) >= EXCESSIVE_ANNUAL_HOURS
  );
  const negative = annualRows.filter((person) => (person.lb_annual as number) < 0);
  const zero = annualRows.filter(
    (person) => person.lb_annual === 0 && person.lb_personal === 0
  );
  const missing = nonCasual.filter((person) => person.lb_annual === null);

  const riskRows = withBalance
    .filter((person) => !(person.lb_annual === 0 && person.lb_personal === 0))
    .sort((a, b) => (b[balanceKey] as number) - (a[balanceKey] as number));

  const byEmployee = riskRows.map((person) => {
    const tone = leaveRiskTone(person.lb_annual, person.lb_personal);
    return {
      key: String(person.id),
      label: person.name,
      value: Math.max(0, (person[balanceKey] as number) || 0),
      color: TONE_COLOURS[tone] || 'var(--chart-3)',
      hint: `${person.dept || 'No department'}\nAnnual ${formatNumber(
        person.lb_annual,
        1
      )} hrs · Personal ${formatNumber(person.lb_personal, 1)} hrs`,
    };
  });

  const byDepartment = sortCountsDesc(
    withBalance.reduce((counts, person) => {
      const department = person.dept || 'Not recorded';
      counts.set(department, (counts.get(department) || 0) + ((person[balanceKey] as number) || 0));
      return counts;
    }, new Map<string, number>())
  ).map(([label, value]) => ({
    key: label,
    label,
    value: Math.round(value),
    hint: `${formatNumber(value, 1)} ${leaveType.toLowerCase()} hours`,
  }));

  const missingColumns: HrColumn<HrPerson>[] = [
    { key: 'name', header: 'Name', value: (person) => person.name },
    { key: 'dept', header: 'Department', value: (person) => person.dept || '—' },
    { key: 'role', header: 'Job role', value: (person) => person.role || '—' },
    {
      key: 'emp',
      header: 'Employment type',
      value: (person) => person.emp || 'Not recorded',
      render: (person) =>
        person.emp ? (
          person.emp
        ) : (
          <HrStatusPill tone="warning">Not recorded</HrStatusPill>
        ),
    },
    { key: 'start', header: 'Start', value: (person) => person.start || '—' },
  ];

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: `Total ${leaveType.toLowerCase()} leave`,
            value: formatNumber(Math.round(total)),
            unit: 'hrs',
            detail: `≈ ${formatNumber(Math.round(total / STANDARD_DAY_HOURS))} days at ${
              STANDARD_DAY_HOURS
            } hrs`,
            icon: <Clock className="h-5 w-5" />,
          },
          {
            label: 'Average balance',
            value: average === null ? '—' : formatNumber(Math.round(average)),
            unit: 'hrs',
            detail: `${withBalance.length} non-casual staff with a balance`,
            icon: <Users className="h-5 w-5" />,
          },
          {
            label: 'Excessive (≥ 2 yrs)',
            value: excessive.length,
            detail: `≥ ${EXCESSIVE_ANNUAL_HOURS} hrs annual`,
            tone: excessive.length > 0 ? 'danger' : 'success',
            icon: <Banknote className="h-5 w-5" />,
          },
          {
            label: 'Negative balance',
            value: negative.length,
            tone: negative.length > 0 ? 'danger' : 'success',
            icon: <CircleMinus className="h-5 w-5" />,
          },
          {
            label: 'Zero / not configured',
            value: zero.length,
            detail: 'Accruals likely not set',
            tone: zero.length > 0 ? 'warning' : 'primary',
            icon: <CircleSlash className="h-5 w-5" />,
          },
          {
            label: 'No balance on file',
            value: missing.length,
            detail: 'Non-casual staff · chase',
            tone: missing.length > 0 ? 'warning' : 'primary',
            icon: <Users className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">{leaveType} leave by employee</CardTitle>
                <CardDescription>
                  Hours accrued · colour flags liability risk
                </CardDescription>
              </div>
              <Tabs value={leaveType} onValueChange={(value) => setLeaveType(value as LeaveType)}>
                <TabsList>
                  <TabsTrigger value="Annual">Annual</TabsTrigger>
                  <TabsTrigger value="Personal">Personal</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <HrBarList
              items={byEmployee}
              formatter={(value) => formatNumber(Math.round(value))}
              unit="hrs"
            />
            {leaveType === 'Annual' ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-chart-3" /> Under watch threshold
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-warning" /> {WATCH_ANNUAL_HOURS}+ hrs · watch
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-destructive" /> {EXCESSIVE_ANNUAL_HOURS}+ hrs · action
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-destructive" /> Negative · review
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Liability by department</CardTitle>
            <CardDescription>
              {leaveType} hours · select a bar to filter every page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <HrBarList
              items={byDepartment}
              onSelect={toggleDept}
              selectedKeys={filters.dept}
              formatter={(value) => formatNumber(value)}
              unit="hrs"
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              <strong className="font-medium text-foreground">Scope:</strong>{' '}
              {people.length - nonCasual.length} casual employees excluded, as they don’t accrue
              leave.
              <br />
              <strong className="font-medium text-foreground">Coverage:</strong>{' '}
              {withBalance.length} of {nonCasual.length} non-casual employees have a balance (
              {percent(withBalance.length, nonCasual.length)}%).
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Non-casual staff with no leave balance</CardTitle>
          <CardDescription>
            Confirm employment type in Idarah; if permanent, set up accruals
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3">
          <HrDataTable
            rows={missing}
            columns={missingColumns}
            initialSort={{ key: 'dept', direction: 'asc' }}
            searchPlaceholder="Search staff…"
            emptyLabel="Every non-casual employee has a balance"
            viewName="No leave balance"
            filters={filters}
            asOf={asOf}
            canExport={canExport}
            onBeforeExport={checkExport}
            rowKey={(person) => person.id}
          />
        </CardContent>
      </Card>
    </div>
  );
}
